// Detection automatique de l'ecriture utilisee dans un texte, a partir des
// plages Unicode de chaque caractere -- pas de dependance externe, fonctionne
// aussi bien cote serveur (Node) que cote navigateur (meme code, aucune API
// specifique a l'un ou l'autre).
//
// Objectif : quand un utilisateur tape une punchline, determiner tout seul
// quelle est l'ecriture utilisee (arabe, devanagari, thai...) pour appliquer
// automatiquement la bonne police et le bon pipeline knockout -- sans lui
// demander de choisir quoi que ce soit ("scenario A" evoque avec Rudy).
//
// Usage :
//   import { detectScript, SCRIPT_CONFIG } from "./detectScript.mjs";
//   const result = detectScript("جميلة رغم كل شيء");
//   // result.script === "Arab"
//   const config = SCRIPT_CONFIG[result.script];
//   // config.font, config.hbScript, config.hbLanguage, config.direction, ...

// ---------- 1. plages Unicode par ecriture ----------
// Volontairement larges (blocs principaux + extensions courantes) plutot
// qu'exhaustives caractere par caractere -- suffisant pour detecter
// l'ecriture dominante d'une punchline.
const RANGES = {
  Arab: [
    [0x0600, 0x06ff], [0x0750, 0x077f], [0x08a0, 0x08ff],
    [0xfb50, 0xfdff], [0xfe70, 0xfeff],
  ],
  Hebr: [[0x0590, 0x05ff]],
  Deva: [[0x0900, 0x097f]],
  Beng: [[0x0980, 0x09ff]],
  Thai: [[0x0e00, 0x0e7f]],
  Cyrl: [[0x0400, 0x04ff], [0x0500, 0x052f]],
  Grek: [[0x0370, 0x03ff], [0x1f00, 0x1fff]],
  Hang: [[0xac00, 0xd7a3], [0x1100, 0x11ff], [0x3130, 0x318f]],
  Hani: [[0x4e00, 0x9fff], [0x3400, 0x4dbf]], // hanzi/kanji -- voir fusion Jpan/Hani plus bas
  // Hiragana + katakana (incl. demi-chasse) : PROPRES au japonais, jamais
  // utilises en chinois -- leur seule presence sert de signal fiable pour
  // distinguer les deux ecritures qui partagent sinon les memes kanji/hanzi
  // (probleme d'unification Han, cf. fusion plus bas dans detectScript()).
  Jpan: [[0x3040, 0x309f], [0x30a0, 0x30ff], [0xff65, 0xff9f]],
  Latn: [[0x0041, 0x005a], [0x0061, 0x007a], [0x00c0, 0x024f]],
};

// caracteres "neutres" qu'on ignore dans le comptage : espaces, ponctuation,
// chiffres -- ils n'appartiennent a aucune ecriture en particulier et
// fausseraient la detection s'ils etaient comptes
function isNeutral(codePoint) {
  const c = String.fromCodePoint(codePoint);
  return /[\s\p{P}\p{N}\p{S}]/u.test(c);
}

function scriptOf(codePoint) {
  for (const [script, ranges] of Object.entries(RANGES)) {
    for (const [start, end] of ranges) {
      if (codePoint >= start && codePoint <= end) return script;
    }
  }
  return null; // ecriture non geree / inconnue
}

/**
 * Detecte l'ecriture dominante d'un texte.
 * @param {string} text
 * @returns {{
 *   script: string,              // tag HarfBuzz de l'ecriture dominante ("Arab", "Latn", ...)
 *   counts: Record<string,number>, // repartition des caracteres par ecriture (hors caracteres neutres)
 *   mixed: boolean,               // true si au moins 2 ecritures sont significativement presentes
 * }}
 */
export function detectScript(text) {
  const counts = {};
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (isNeutral(cp)) continue;
    const script = scriptOf(cp);
    if (!script) continue;
    counts[script] = (counts[script] || 0) + 1;
  }

  // Des qu'au moins un caractere kana (hiragana/katakana) est present, les
  // kanji comptes a part (plage Hani, partagee avec le chinois -- probleme
  // d'unification Han, pas de plage Unicode "japonais" a elle seule pour
  // les ideogrammes) sont en realite du japonais eux aussi : on les
  // reattribue a Jpan plutot que de les laisser fausser la detection vers
  // "Chinois" sur une phrase qui n'en est pas une (ex. "余分なのように",
  // constate par Rudy, 24 septembre 2026 -- classee Hani car seuls les 2
  // kanji etaient comptes, les 5 kana ignores faute de plage geree a
  // l'epoque). Sans kana, un texte 100% kanji reste Hani (chinois) --
  // aucun moyen fiable de trancher autrement, comportement inchange.
  if (counts.Jpan && counts.Hani) {
    counts.Jpan += counts.Hani;
    delete counts.Hani;
  }

  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    // rien de reconnu (texte vide, uniquement neutre, ou ecriture non geree) -> repli latin
    return { script: "Latn", counts, mixed: false };
  }

  const [topScript, topCount] = entries[0];
  const totalMeaningful = entries.reduce((sum, [, n]) => sum + n, 0);
  // "mixte" si l'ecriture dominante represente moins de 90% des caracteres
  // significatifs -- seuil arbitraire, a ajuster selon les retours d'usage reel
  const mixed = topCount / totalMeaningful < 0.9;

  return { script: topScript, counts, mixed };
}

// ---------- 2. config centralisee par ecriture ----------
// Rassemble tout ce qui a ete valide le 7 septembre : police retenue (Bold 700,
// choisie apres comparatif avec Black 900 -- voir docs/alphabet-knockout-passation-2026-09-07.md), tags de shaping
// HarfBuzz, sens de lecture, et reglages du pipeline (UPSCALE / CLOSING_RADIUS /
// SMOOTH_RADIUS) par ecriture. Source de verite unique a reutiliser partout
// plutot que de dupliquer ces valeurs a plusieurs endroits.
export const SCRIPT_CONFIG = {
  Latn: {
    label: "Latin",
    font: "No Futura",
    fontFile: null, // police existante du site, pas fournie ici
    hbScript: "Latn", hbLanguage: "fr", direction: "ltr",
    // pas de pipeline HarfBuzz/potrace pour le latin : effet dessine dans les
    // glyphes de No Futura directement (voir No Futura Bold Underlined)
  },
  Arab: {
    label: "Arabe",
    font: "Cairo", fontFile: "../assets/fonts/Cairo-Black.ttf",
    hbScript: "Arab", hbLanguage: "ar", direction: "rtl",
    weight: 700, upscale: 8, closingRadius: 25, smoothRadius: 8,
    // 8 septembre (demande Rudy, constat sur les phrases test de la galerie) :
    // la boite knockout doit faire toute la hauteur de la phrase (comme le
    // canevas d'animation), pas juste la hauteur du mot -- voir
    // boxFullPhraseHeight dans build-knockout.mjs.
    boxFullPhraseHeight: true,
    // Precalcul par lettre (demande Rudy, 19 septembre 2026, prototype valide
    // -- 0.29% de pixels differents vs le rendu par groupe, aucune coupure de
    // connexion entre lettres) : chaque glyphe HarfBuzz (deja resolu a sa
    // forme contextuelle -- initiale/mediane/finale/isolee -- et aux
    // ligatures par le shaping) est fusionne UNE FOIS et mis en cache de
    // facon persistante (alphabet/glyphCache.mjs), plutot que refusionner
    // toute la phrase a chaque generation -- voir
    // fuseGroupViaGlyphCache() dans build-knockout.mjs. Prototype pas encore
    // etendu aux 8 autres ecritures (chacune a ses propres specificites de
    // jonction entre lettres a revalider avant d'activer ce chemin).
    useGlyphCache: true,
  },
  Deva: {
    label: "Devanagari",
    font: "Poppins", fontFile: "../assets/fonts/Poppins-Bold.ttf",
    hbScript: "Deva", hbLanguage: "hi", direction: "ltr",
    weight: null, upscale: 8, closingRadius: 30, smoothRadius: 8, // police statique Bold, pas d'axe de variation
    boxFullPhraseHeight: true,
    // Precalcul PAR LETTRE teste et REJETE (Rudy, 19 septembre 2026) : le
    // devanagari a une ligne horizontale (shirorekha) qui relie visuellement
    // les lettres d'un meme mot en un seul trait continu -- fusionnee glyphe
    // par glyphe (chaque lettre isolement), cette ligne se retrouve tronquee
    // a chaque jonction, coupures visibles confirmees par comparaison pixel a
    // pixel (0.716% de difference, 9 zones de rupture de 9-10 pixels de
    // large -- contre <0.1% et 1-2px de bruit pour les ecritures qui passent
    // par glyphe). Precalcul PAR MOT ENTIER en revanche valide (meme jour) :
    // le mot est rasterise d'un bloc, comme l'ancien pipeline mais a
    // l'echelle du mot plutot que de tout le groupe -- la shirorekha reste
    // continue (0.104% de difference, aucune coupure, meme niveau de bruit
    // que le precalcul par lettre ailleurs). Cache moins souvent "chaud" au
    // demarrage qu'un alphabet fini (bien plus de mots que de lettres), mais
    // grandit avec l'usage reel -- voir fuseGroupViaWordCache() dans
    // build-knockout.mjs.
    useWordCache: true,
  },
  Hani: {
    label: "Chinois",
    font: "Noto Sans SC", fontFile: "../assets/fonts/NotoSansSC-Black.otf",
    hbScript: "Hani", hbLanguage: "zh", direction: "ltr",
    weight: 700, upscale: 12, closingRadius: 10, smoothRadius: 3,
    // 9 septembre (demande Rudy) : limite de taille sur les contre-formes
    // comblees -- sans elle (Infinity partout ailleurs), les radicaux a
    // compartiments (ex. 目 dans "想") deviennent un carre noir plein illisible.
    // Mesure sur "梦想成真" (UPSCALE=12) : les 7 trous reels de cette phrase
    // font tous entre 61 245 et 104 979 px^2 -- 5000 est tres confortablement
    // en-dessous, ne comble que d'eventuels petits residus de fermeture
    // morphologique, jamais une vraie contre-forme de caractere.
    maxHoleArea: 5000,
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- comme le coreen, les caracteres chinois sont deja des glyphes
    // isoles (pas de trait de connexion entre eux). Voir useGlyphCache sur
    // Arab pour le detail de l'approche.
    useGlyphCache: true,
  },
  Cyrl: {
    label: "Cyrillique",
    font: "Unbounded", fontFile: "../assets/fonts/Unbounded-Black.ttf",
    hbScript: "Cyrl", hbLanguage: "ru", direction: "ltr",
    weight: 700, upscale: 8, closingRadius: 40, smoothRadius: 13,
    // limite connue : la jonction "к"+"р" (et paires similaires a chevauchement
    // large) garde un residu tres mineur, visible seulement a fort zoom -- voir
    // docs/alphabet-knockout-passation-2026-09-07.md. Un traitement cible par paire de lettres serait la seule
    // vraie garantie de perfection sur ce point, non fait a ce jour.
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- risque different du devanagari/bengali (pas de ligne
    // connectrice) mais bold display font avec chevauchement pixel reel entre
    // lettres adjacentes (voir le residu "к"+"р" documente ci-dessus, deja
    // imparfait meme AVEC fusion par groupe) -- la fusion par glyphe seul
    // pourrait aggraver ce residu ou en creer de nouveaux. A verifier par
    // comparaison pixel a pixel avant d'etendre.
    useGlyphCache: true,
  },
  Beng: {
    label: "Bengali",
    font: "Noto Sans Bengali", fontFile: "../assets/fonts/NotoSansBengali-Black.ttf",
    hbScript: "Beng", hbLanguage: "bn", direction: "ltr",
    weight: 700, upscale: 10, closingRadius: 30, smoothRadius: 8,
    // 9 septembre (demande Rudy, oubli lors du reglage du 8 septembre) :
    // meme defaut que arabe/devanagari/thai/grec -- signes voyelle au-dessus
    // ou en-dessous du mot knockout non couverts par une boite cadree sur le
    // mot seul, voir boxFullPhraseHeight dans build-knockout.mjs.
    boxFullPhraseHeight: true,
    // Precalcul PAR LETTRE teste et REJETE (Rudy, 19 septembre 2026) : meme
    // probleme que le devanagari -- la matra (ligne horizontale reliant les
    // lettres d'un mot) se retrouve coupee a chaque jonction quand chaque
    // glyphe est fusionne isolement (0.136%, 12 zones de rupture de 3-7
    // pixels). Precalcul PAR MOT ENTIER valide en revanche (meme principe que
    // Deva ci-dessus) : le mot est rasterise d'un bloc, la matra reste
    // continue -- voir fuseGroupViaWordCache() dans build-knockout.mjs.
    useWordCache: true,
  },
  Hebr: {
    label: "Hebreu",
    font: "Rubik", fontFile: "../assets/fonts/Rubik-Black.ttf",
    hbScript: "Hebr", hbLanguage: "he", direction: "rtl",
    weight: 700, upscale: 8, closingRadius: 40, smoothRadius: 10,
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- l'hebreu n'a pas de traits de connexion entre lettres (pas
    // d'ecriture cursive, chaque lettre est un bloc isole, comme le
    // coreen/chinois deja valides). Voir useGlyphCache sur Arab pour le
    // detail de l'approche.
    useGlyphCache: true,
  },
  Thai: {
    label: "Thai",
    font: "Kanit", fontFile: "../assets/fonts/Kanit-Bold.ttf",
    hbScript: "Thai", hbLanguage: "th", direction: "ltr",
    weight: null, upscale: 8, closingRadius: 15, smoothRadius: 4, // police statique Bold
    boxFullPhraseHeight: true,
    // 9 septembre (demande Rudy) : liseret restant en bas (lettres arrondies
    // type "U") -- seuil fixe insuffisant, voir edgeTrimWidthFraction dans
    // build-knockout.mjs/morphology.mjs.
    edgeTrimWidthFraction: 0.6,
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- le thai a des signes voyelle/ton qui se superposent
    // visuellement au-dessus/en-dessous de la lettre de base : premier vrai
    // test de si la fusion par GLYPHE INDIVIDUEL (chaque glyphe HarfBuzz,
    // deja positionne par le GPOS de la police -- signe + consonne = souvent
    // 2 glyphes distincts) suffit, ou si une fusion inter-glyphes etait
    // necessaire. A verifier par comparaison pixel a pixel avant d'etendre.
    useGlyphCache: true,
  },
  Hang: {
    label: "Coreen",
    // 7 septembre : Black Han Sans n'a qu'une seule graisse (pas d'axe de
    // variation), remplacee par Noto Sans KR. 8 septembre (demande Rudy) :
    // remplacee a nouveau par IBM Plex Sans KR (libre de droits, ~880 Ko en
    // statique Bold contre 10 Mo pour Noto Sans KR variable) -- police
    // statique elle aussi (verifie : aucun axe de variation), meme situation
    // que Poppins/Kanit -> weight: null. CLOSING_RADIUS/SMOOTH_RADIUS
    // recalibres pour cette police specifiquement (voir
    // docs/alphabet-knockout-passation-2026-09-07.md) : les reglages valides
    // pour Noto Sans KR ne sont pas transposables tels quels a une autre
    // police, deja verifie plusieurs fois cette session (Bold vs Black).
    font: "IBM Plex Sans KR", fontFile: "../assets/fonts/IBMPlexSansKR-Bold.otf",
    hbScript: "Hang", hbLanguage: "ko", direction: "ltr",
    weight: null, upscale: 10, closingRadius: 15, smoothRadius: 4,
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- le coreen n'a pas de traits de connexion entre lettres (chaque
    // bloc syllabique est un glyphe deja isole), donc peu de risque de casser
    // une jonction. Voir useGlyphCache sur Arab (deja valide+en prod) pour le
    // detail de l'approche.
    useGlyphCache: true,
  },
  Grek: {
    label: "Grec",
    // 8 septembre (demande Rudy) : remplacee par Akt (variable, axe wght
    // 100-900, reglee a 700 pour rester coherente avec le standard Bold du
    // projet -- meme logique que Cyrl/Unbounded). CLOSING_RADIUS/SMOOTH_RADIUS
    // recalibres specifiquement pour cette police (voir commentaire Hang
    // ci-dessus : jamais transposables tels quels d'une police a l'autre).
    font: "Akt", fontFile: "../assets/fonts/Akt-Variable.ttf",
    hbScript: "Grek", hbLanguage: "el", direction: "ltr",
    weight: 700, upscale: 8, closingRadius: 15, smoothRadius: 4,
    boxFullPhraseHeight: true,
    // 9 septembre (demande Rudy) : liseret restant en haut du cadrage a
    // cause des accents (tonos) -- voir trimAccentsFromEdge dans
    // build-knockout.mjs, "on rogne un peu l'accent" explicitement approuve.
    trimAccentsFromEdge: true,
    // Precalcul par lettre EN COURS DE VALIDATION (demande Rudy, 19 septembre
    // 2026) -- pas de trait de connexion entre lettres grecques (contrairement
    // au cyrillique, dont la police bold cree de vrais chevauchements pixel a
    // pixel entre lettres adjacentes -- voir Cyrl plus bas). trimAccentsFromEdge
    // ci-dessus reste inchange : il opere sur les items bruts de chaque groupe
    // (position/bounds/char), pas sur le resultat fusionne. Voir useGlyphCache
    // sur Arab pour le detail de l'approche.
    useGlyphCache: true,
    // Liseret restant en bas (descendante du "ρ") -- meme cause/solution que
    // le thai, voir edgeTrimWidthFraction dans build-knockout.mjs.
    edgeTrimWidthFraction: 0.6,
  },
  Jpan: {
    label: "Japonais",
    // 24 septembre 2026 (demande Rudy) : IBM Plex Sans JP, meme choix que le
    // coreen (IBM Plex Sans KR) et pour la meme raison -- police statique
    // Bold legere (~2,3 Mo) plutot que Noto Sans JP (variable mais >15 Mo,
    // meme probleme de poids que l'ancien choix ecarte pour le coreen).
    // Couverture verifiee (opentype.js) : kanji ET hiragana/katakana tous
    // presents dans le fichier.
    font: "IBM Plex Sans JP", fontFile: "../assets/fonts/IBMPlexSansJP-Bold.ttf",
    hbScript: "Jpan", hbLanguage: "ja", direction: "ltr",
    // Reglages CLOSING_RADIUS/SMOOTH_RADIUS/UPSCALE de depart, alignes sur
    // Hang (coreen) : meme famille de police (IBM Plex Sans, statique,
    // aucun axe de variation -> weight: null), et memes caracteristiques de
    // forme -- glyphes deja isoles (kanji ET kana n'ont pas de trait de
    // connexion entre eux, contrairement a l'arabe/devanagari), blocs
    // relativement denses comme le coreen/chinois. Point de depart
    // raisonnable mais PAS verifie pixel a pixel comme les 9 autres
    // ecritures (qui ont chacune eu leur propre passe de calibration/
    // comparaison avec Rudy, cf. docs/alphabet-knockout-passation-2026-09-07.md)
    // -- a valider visuellement avant de considerer ces valeurs definitives.
    weight: null, upscale: 10, closingRadius: 15, smoothRadius: 4,
    // Comme le coreen/chinois/hebreu : glyphes isoles (pas de trait de
    // connexion), le precalcul PAR LETTRE (plutot que par mot ou par
    // groupe) devrait s'appliquer sans le risque de coupure documente sur
    // devanagari/bengali (shirorekha/matra) -- meme raisonnement que
    // useGlyphCache sur Hang/Hebr/Hani, pas re-verifie specifiquement pour
    // le japonais.
    useGlyphCache: true,
  },
};
